import { Check, ChevronDown, ListFilter } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type SelectTone = "neutral" | "primary" | "success" | "warning" | "danger";

export interface SelectOption {
  value: string;
  label: string;
  /** A coloured dot before the label, e.g. a status or priority colour. */
  tone?: SelectTone | undefined;
  /** A short second line saying what the option means. */
  detail?: string | undefined;
}

export interface SelectGroup {
  /** A small heading over the group (none for the first, unlabelled group). */
  label?: string;
  options: SelectOption[];
}

interface SelectProps {
  /** What it chooses, for screen readers and the menu (e.g. "Status"). */
  label: string;
  /** For a visible <label htmlFor> beside it, as on a form field. */
  id?: string;
  disabled?: boolean;
  value: string;
  groups: SelectGroup[];
  onChange: (value: string) => void;
  /** Shown when the value matches no option (e.g. "Saved views…"). */
  placeholder?: string;
  /** The "no filter" value: anything else is shown as an active filter. */
  neutralValue?: string;
  size?: "sm" | "md";
  /** "icon": a small filter button beside a column heading instead of a full field. */
  variant?: "field" | "icon";
  className?: string;
  icon?: ReactNode;
}

const GAP = 4;
const MENU_MAX_HEIGHT = 320;
const MENU_MIN_WIDTH = 200;

/**
 * A dropdown that looks like the rest of the app, unlike the browser's own. Keyboard: Enter,
 * Space or the arrow keys open it; arrows, Home and End move; typing jumps to a match; Enter
 * picks; Escape closes. The menu sits in a layer above everything (so a scrolling table
 * can't clip it), inside any modal dialog it's in, and stays inside the window.
 */
export function Select({
  label,
  id: triggerId,
  disabled = false,
  value,
  groups,
  onChange,
  placeholder,
  neutralValue,
  size = "md",
  variant = "field",
  className,
  icon,
}: SelectProps) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Inside a modal dialog the menu must be in the dialog too: the page behind it is inert.
  const [host, setHost] = useState<Element | null>(null);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; above: boolean } | null>(null);
  const typed = useRef({ text: "", at: 0 });

  const options = useMemo(() => groups.flatMap((g) => g.options), [groups]);
  // Where each group's options start in the flat list (for ids and the active option).
  const offsets = useMemo(
    () => groups.reduce<number[]>((acc, _g, i) => [...acc, i === 0 ? 0 : (acc[i - 1] ?? 0) + (groups[i - 1]?.options.length ?? 0)], []),
    [groups],
  );
  const selected = options.find((o) => o.value === value);
  const filtered = neutralValue !== undefined && value !== neutralValue && selected !== undefined;

  const place = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, MENU_MIN_WIDTH);
    const below = window.innerHeight - rect.bottom;
    const above = below < Math.min(MENU_MAX_HEIGHT, menu.current?.offsetHeight ?? MENU_MAX_HEIGHT) && rect.top > below;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setPosition({ top: above ? rect.top - GAP : rect.bottom + GAP, left, width, above });
  }, []);

  function show() {
    setActive(Math.max(0, options.findIndex((o) => o.value === value)));
    setHost(trigger.current?.closest("dialog") ?? document.body);
    setOpen(true);
  }

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  }, []);

  function pick(option: SelectOption) {
    close();
    if (option.value !== value) onChange(option.value);
  }

  useLayoutEffect(() => {
    if (!open) return;
    place();
    menu.current?.focus();
  }, [open, place]);

  // Keep it attached to its button while the page scrolls; close it when clicking elsewhere.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menu.current?.contains(target) && !trigger.current?.contains(target)) close(false);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open, place, close]);

  // The active option stays in view as the arrows move through a long list.
  useEffect(() => {
    if (open) document.getElementById(`${id}-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [open, active, id]);

  function onTriggerKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      show();
    }
  }

  function onMenuKey(event: KeyboardEvent<HTMLDivElement>) {
    const last = options.length - 1;
    if (event.key === "ArrowDown") setActive((i) => Math.min(last, i + 1));
    else if (event.key === "ArrowUp") setActive((i) => Math.max(0, i - 1));
    else if (event.key === "Home") setActive(0);
    else if (event.key === "End") setActive(last);
    else if (event.key === "Enter" || event.key === " ") {
      const option = options[active];
      if (option) pick(option);
    } else if (event.key === "Escape") close();
    else if (event.key === "Tab") {
      close(false);
      return; // let the focus move on
    } else if (event.key.length === 1 && /\S/.test(event.key)) {
      // Type-ahead: jump to the next option starting with what's been typed.
      const now = Date.now();
      const text = (now - typed.current.at < 700 ? typed.current.text : "") + event.key.toLowerCase();
      typed.current = { text, at: now };
      const from = text.length === 1 ? active + 1 : active;
      const order = [...options.slice(from), ...options.slice(0, from)];
      const hit = order.find((o) => o.label.toLowerCase().startsWith(text));
      if (hit) setActive(options.indexOf(hit));
    } else return;
    event.preventDefault();
  }

  return (
    <>
      <button
        ref={trigger}
        id={triggerId}
        type="button"
        disabled={disabled}
        className={(variant === "icon"
          ? ["col-filter", filtered ? "col-filter-active" : "", className ?? ""]
          : ["select-trigger", `select-trigger-${size}`, filtered ? "select-trigger-active" : "", className ?? ""]
        )
          .filter(Boolean)
          .join(" ")}
        title={variant === "icon" ? `Filter by ${label.toLowerCase()}${filtered ? `: ${selected?.label ?? ""}` : ""}` : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${id}-menu` : undefined}
        aria-label={`${label}: ${selected?.label ?? placeholder ?? "none"}`}
        onClick={() => (open ? close() : show())}
        onKeyDown={onTriggerKey}
      >
        {variant === "icon" ? (
          <ListFilter size={14} aria-hidden="true" />
        ) : (
          <>
            {icon}
            {selected?.tone ? <span className={`select-dot select-dot-${selected.tone}`} aria-hidden="true" /> : null}
            <span className={selected ? "select-value" : "select-value select-placeholder"}>
              {selected?.label ?? placeholder ?? ""}
            </span>
            <ChevronDown className="select-chevron" size={16} aria-hidden="true" />
          </>
        )}
      </button>
      {open && host
        ? createPortal(
            <div
              ref={menu}
              id={`${id}-menu`}
              role="listbox"
              aria-label={label}
              aria-activedescendant={`${id}-${active}`}
              tabIndex={-1}
              className={position?.above ? "select-menu select-menu-above" : "select-menu"}
              style={
                position
                  ? { top: position.top, left: position.left, minWidth: position.width }
                  : { visibility: "hidden" }
              }
              onKeyDown={onMenuKey}
            >
              {groups.map((group, g) => (
                <div key={group.label ?? g} role="group" aria-label={group.label} className="select-group">
                  {group.label ? (
                    <div className="select-group-label" aria-hidden="true">
                      {group.label}
                    </div>
                  ) : null}
                  {group.options.map((option, j) => {
                    const i = (offsets[g] ?? 0) + j;
                    const isSelected = option.value === value;
                    return (
                      <div
                        key={option.value}
                        id={`${id}-${i}`}
                        role="option"
                        aria-selected={isSelected}
                        className={i === active ? "select-option select-option-active" : "select-option"}
                        onPointerMove={() => setActive(i)}
                        onClick={() => pick(option)}
                      >
                        {option.tone ? (
                          <span className={`select-dot select-dot-${option.tone}`} aria-hidden="true" />
                        ) : null}
                        {option.detail ? (
                          <span className="select-option-text">
                            <span className="select-option-label">{option.label}</span>
                            <span className="select-option-detail">{option.detail}</span>
                          </span>
                        ) : (
                          <span className="select-option-label">{option.label}</span>
                        )}
                        {isSelected ? <Check className="select-check" size={16} aria-hidden="true" /> : null}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>,
            host,
          )
        : null}
    </>
  );
}
